import { AbstractShape } from "../abstract/Shape";
import { IField } from "../interfaces/Field";
import { SimulationNodeBlock, TaskDurationVariability } from "../interfaces/Simulation";
import { nextTaskType, previusTaskDepencency } from "../utils/SimulationUtils";
import { ControlSlider } from "./ControlSlider";

export type SimulationNodeDataRequest = [
    stepId: number,
    nodeUUID: string,
    nodeName: string
];

export interface SimulationNodeData {
    task_order: number;
    task_UUID: string;
    task_name: string;
    previous_task: number[];
    previous_task_dependency_type: string;
    next_task: number[];
    next_task_type: string;
    isTerminal: boolean;
    total_events: number | null;
    min_duration: number | null;
    max_duration: number | null;
    avg_duration: number | null;
    sample_std_dev: number | null;
    population_std_dev: number | null;
    median_duration: number | null;
    mode_duration: number | null;
    mode_frequency: number | null;
}

export interface SimulationNodesDataSummary {
    nodes_created: number;
    nodes_deleted: number;
    labels_added: number;
    labels_removed: number;
    relationships_created: number;
    relationships_deleted: number;
    properties_set: number;
    contains_updates: boolean;
    "seconds_to_produce_results ": number;
    seconds_to_consume_all_results: number;
}

export interface SimulationNodesDataResponse {
    status: number;
    elements: number;
    result: SimulationNodeData[];
    summary: SimulationNodesDataSummary;
}

export class SimulationValues {

    task_order: number;
    task_name: string;
    task_UUID: string;
    previous_task: number[] = [];
    next_task: number[] = [];

    nextTaskProb: boolean = false;

    taskProbability: IField[] = [];

    constructor(node: AbstractShape, drawFunction?: Function){
        this.drawFunction = drawFunction;
        this._workersSlider = new ControlSlider("Workers", "n°", 1, 10, 1, this.drawFunction);
        this._timeSlider = new ControlSlider("Time %", "%", -100, 100, 0, this.drawFunction);
        this._waitSlider = new ControlSlider("Max Wait Time", "n°", 0, 100, 0, this.drawFunction);
        this.task_name = node.name;
        this.task_UUID = node.dbPropierties.__EDT_UUID__;
        this.task_order = 100;
    }

    drawFunction!: Function | undefined;

    _workersSlider: ControlSlider;
    _timeSlider: ControlSlider;
    _waitSlider: ControlSlider;
    previusTaskValue: IField = previusTaskDepencency[0];
    nextTaskValue: IField = nextTaskType[0];
    is_terminal: boolean = false;
    _task_duration_avg: number = 0;
    task_duration_stdev: number | null = null;
    task_duration_variability: TaskDurationVariability = 'moderate';
    no_worker: boolean = true;
    resource_id: string | null = null;
    resource_units: number = 1;
    queue_priority: number = 100;
    group: string = "";
    group_assignation: string = "";
    allow_abandon: boolean = false;
    cust_alias: string =  "";

    set workersSlider(control: ControlSlider){
        this._workersSlider = control;
        this.draw();
    }

    get workersSlider(){
        return this._workersSlider;
    }

    set timeSlider(control: ControlSlider){
        this._timeSlider = control;
        this.draw();
    }

    get timeSlider(){
        return this._timeSlider;
    }

    set waitSlider(control: ControlSlider){
        this._waitSlider = control;
        this.draw();
    }

    get waitSlider(){
        return this._waitSlider;
    }

    set task_duration_avg(value: number){
        this._task_duration_avg = value;
        this.draw();
    }

    get task_duration_avg(){
        return this._task_duration_avg;
    }

    draw(){
        if(this.drawFunction) this.drawFunction();
    }

    applyNodeData(data: SimulationNodeData) {
        this.task_order = data.task_order;
        this.task_name = data.task_name;
        this.task_UUID = data.task_UUID;
        this.previous_task = [...data.previous_task];
        this.next_task = [...data.next_task];
        this.previusTaskValue = previusTaskDepencency.find((option) => {
            return option.name === data.previous_task_dependency_type;
        }) || this.previusTaskValue;
        this.nextTaskValue = nextTaskType.find((option) => {
            return option.name === data.next_task_type;
        }) || this.nextTaskValue;
        this.nextTaskProb = this.nextTaskValue.name === nextTaskType[1].name;
        this.is_terminal = data.isTerminal;

        if (data.avg_duration !== null) {
            this.task_duration_avg = data.avg_duration;
        }

        const taskDurationStandardDeviation = data.sample_std_dev ?? data.population_std_dev;
        if (taskDurationStandardDeviation !== null) {
            this.task_duration_stdev = taskDurationStandardDeviation;
        }
    }

    private getNextTaskWithProbability(): [number, number][] {
        return this.taskProbability
            .map((prob) => [Number(prob.value), Number(prob.name)] as [number, number])
            .filter(([probability, taskOrder]) => Number.isFinite(probability) && Number.isFinite(taskOrder));
    }

    parseIntoSendValues(){
        return {
            task_order: this.task_order,
            task_name: this.task_name,
            task_UUID: this.task_UUID,
            task_duration_avg: Number(this.task_duration_avg),
            task_duration_stdev: this.task_duration_stdev,
            task_duration_variability: this.task_duration_variability,
            workers: this.no_worker ? null : this.workersSlider.value,
            resource_id: this.resource_id?.trim() || null,
            resource_units: this.resource_units,
            queue_priority: this.queue_priority,
            allow_abandon: this.allow_abandon,
            max_wait_time: this.waitSlider.value,
            previous_task: [...this.previous_task],
            previous_task_dependency_type: this.previusTaskValue.name,
            next_task_type: this.nextTaskValue.name,
            next_task: (this.nextTaskValue.name === nextTaskType[1].name) ? this.getNextTaskWithProbability() : [...this.next_task],
            is_terminal: this.is_terminal,
            cust_alias: this.task_name + this.task_UUID,
            ...(this.group.trim() ? { group: this.group.trim() } : {}),
            ...(this.group_assignation.trim() ? { group_assignation: this.group_assignation.trim() } : {}),
        } as SimulationNodeBlock
    }
}
